import { useState, useEffect, useMemo } from "react"
import { useAppSelector } from "../../app/hooks"
import {
    fetchJobDetail,
    updateRecruiterMatchAction,
    toggleFeedbackStatus,
    type CandidateDetail,
    type JobListItem,
} from "../../features/jobs/jobAPI"
import { toast } from "sonner"
import { DatePicker } from "../date-picker"
import { ConfirmActionModal } from "../confirm-action-modal"

const toDisplayString = (value: unknown): string => {
    if (value == null) return ""
    if (typeof value === "string") return value
    if (typeof value === "number" || typeof value === "boolean") return String(value)
    if (typeof value === "object") {
        const record = value as { name?: string; label?: string }
        if (typeof record.name === "string") return record.name
        if (typeof record.label === "string") return record.label
    }
    return ""
}

const normalizeValue = (value?: unknown) =>
    toDisplayString(value).trim().toLowerCase().replace(/[_-]+/g, " ")

export const ProfileOverview = ({
    candidate,
    candidateDetail = null,
    onClose,
    jobId,
    matchId,
    stage = null,
    hasFeedback = false,
    interviewDate = null,
    onInterviewDateSaved,
    showActions = true,
}: {
    candidate: any
    candidateDetail?: CandidateDetail | null
    onClose?: () => void
    jobId?: number | null
    matchId?: number | null
    stage?: string | null
    hasFeedback?: boolean
    interviewDate?: string | null
    onInterviewDateSaved?: (interviewDate: string) => void
    showActions?: boolean
}) => {
    const accessToken = useAppSelector((state) => state.auth.accessToken)
    const userGroups = useAppSelector((state) => state.auth.user?.groups ?? [])
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [isDatePickerOpen, setIsDatePickerOpen] = useState(false)
    const [isConfirmOpen, setIsConfirmOpen] = useState(false)
    const [pendingAction, setPendingAction] = useState<{
        stage: string
        status: string
        title: string
        message: string
        confirmLabel: string
        successMessage: string
        variant: "primary" | "danger"
    } | null>(null)
    const [hasFeedbackState, setHasFeedbackState] = useState<boolean>(!!hasFeedback)
    const [isFeedbackStatusLoading, setIsFeedbackStatusLoading] = useState(false)
    const [jobDetails, setJobDetails] = useState<JobListItem | null>(null)
    const isCompanyHiringManager = userGroups.includes("company_hiring_manager")

    const normalizedStage = (stage ?? "").trim() || null
    const hasInterviewDate =
        !!interviewDate && !Number.isNaN(new Date(interviewDate).getTime())
    const isInterviewInPast =
        hasInterviewDate && new Date(interviewDate as string) < new Date()

    useEffect(() => {
        let isMounted = true

        if (!accessToken || !jobId) {
            setJobDetails(null)
            return
        }

        const loadJobDetails = async () => {
            const result = await fetchJobDetail(jobId, accessToken)
            if (isMounted) {
                setJobDetails(result.data ?? null)
            }
        }

        void loadJobDetails()

        return () => {
            isMounted = false
        }
    }, [accessToken, jobId])

    // Fetch the feedback completion status so the Shortlist button follows the API state.
    useEffect(() => {
        if (!accessToken || !jobId || !candidate?.id) return
        const loadFeedbackStatus = async () => {
            try {
                setIsFeedbackStatusLoading(true)
                const result = await toggleFeedbackStatus(jobId, candidate.id, accessToken)
                setHasFeedbackState(Boolean(result.data?.data?.is_enabled))
            } catch {
                setHasFeedbackState(false)
            } finally {
                setIsFeedbackStatusLoading(false)
            }
        }
        loadFeedbackStatus()
    }, [accessToken, jobId, candidate?.id])

    const isSameValue = (left?: string | null, right?: string | null) =>
        normalizeValue(left).length > 0 && normalizeValue(left) === normalizeValue(right)

    const resolveNationalityRef = (
        nationality?:
            | { id?: number; name?: string }
            | Array<{ id?: number; name?: string }>
            | null
    ) => {
        if (!nationality) return null
        return Array.isArray(nationality) ? nationality[0] ?? null : nationality
    }

    const matchesByIdOrName = (
        candidateItem?: { id?: number; name?: string } | null,
        jobItem?: { id?: number; name?: string } | null
    ) => {
        if (!candidateItem || !jobItem) return false
        if (candidateItem.id && jobItem.id) {
            return candidateItem.id === jobItem.id
        }
        return isSameValue(candidateItem.name, jobItem.name)
    }

    const getBadgeClass = (isMatched: boolean, extraClassName = "") =>
        `inline-block rounded-md border px-3 py-1 text-paragraph font-bold text-black ${isMatched ? "border-[#E6EDDD] bg-[#E6EDDD]" : "border-gray-200 bg-white"} ${extraClassName}`.trim()

    const renderFieldValue = (value: string, isMatched = false, asBadge = false) =>
        asBadge || isMatched ? (
            <span className={getBadgeClass(isMatched)}>{value}</span>
        ) : (
            <span className="text-paragraph font-bold text-black">{value}</span>
        )

    const hasPersonalFieldValue = (value: unknown) => {
        const text = String(value ?? "").trim()
        return text !== "" && text.toLowerCase() !== "n/a"
    }

    const jobSkillNames = useMemo(
        () =>
            new Set(
                (jobDetails?.areas_of_expertise ?? []).flatMap((area) => [
                    ...(area.skills_must_have ?? []).map((skill) => normalizeValue(skill.name)),
                    ...(area.skills_nice_to_have ?? []).map((skill) => normalizeValue(skill.name)),
                ]).filter(Boolean)
            ),
        [jobDetails]
    )

    const jobLanguageRequirements = jobDetails?.language_skills ?? []
    const jobEducationRequirements = jobDetails?.education ?? []
    const jobCertificateNames = useMemo(
        () =>
            new Set(
                (jobDetails?.certificate ?? [])
                    .map((certificate) => normalizeValue(certificate.name))
                    .filter(Boolean)
            ),
        [jobDetails]
    )

    const candidateCertificates = useMemo(() => {
        if (Array.isArray(candidate?.certifications) && candidate.certifications.length > 0) {
            return candidate.certifications.map((cert: {
                name?: unknown
                issuer?: string
                institution?: { name?: string }
                years?: string | number
                year_of_obtension?: number
            }) => ({
                name: toDisplayString(cert?.name ?? cert),
                issuer: cert?.issuer || cert?.institution?.name || "N/A",
                years:
                    cert?.years?.toString() ||
                    cert?.year_of_obtension?.toString() ||
                    "N/A",
            }))
        }

        return (candidateDetail?.education ?? [])
            .filter((education) => education.certificate)
            .map((education) => ({
                name: toDisplayString(education.certificate),
                issuer: education.institution?.name || "N/A",
                years: education.year_of_obtension?.toString() || "N/A",
            }))
    }, [candidate?.certifications, candidateDetail?.education])

    const hasMatchingSkill = (skillName?: string | null) =>
        Boolean(skillName) && jobSkillNames.has(normalizeValue(skillName))

    const getMatchingLanguageRequirement = (languageName?: string | null) =>
        jobLanguageRequirements.find((language) => isSameValue(language.language?.name, languageName))

    const isVisaMatched =
        Boolean(jobDetails?.visa_status) &&
        (candidateDetail?.preferred_locations ?? []).some((location) =>
            isSameValue(location.visa_status, jobDetails?.visa_status)
        )

    const isDrivingLicenseMatched =
        Boolean(jobDetails?.driving_license) &&
        (candidateDetail?.preferred_locations ?? []).some((location) =>
            isSameValue(location.driving_license, jobDetails?.driving_license)
        )

    const jobNationality = resolveNationalityRef(
        jobDetails?.nationality as
            | { id?: number; name?: string }
            | Array<{ id?: number; name?: string }>
            | undefined
    )
    const isNationalityMatched =
        Boolean(jobNationality) &&
        matchesByIdOrName(candidateDetail?.nationality, jobNationality)

    const isEducationMatched = (index: number) => {
        const candidateEducation = candidateDetail?.education?.[index]
        if (!candidateEducation || jobEducationRequirements.length === 0) return false

        return jobEducationRequirements.some((education) => {
            const hasRequirement = Boolean(education.qualification || education.field_of_study)
            if (!hasRequirement) return false

            const qualificationMatches =
                !education.qualification ||
                matchesByIdOrName(candidateEducation.qualification, education.qualification)
            const fieldMatches =
                !education.field_of_study ||
                matchesByIdOrName(candidateEducation.field_of_study, education.field_of_study)

            return qualificationMatches && fieldMatches
        })
    }

    const isCertificationMatched = (certificateName?: unknown) => {
        const label = toDisplayString(certificateName)
        return Boolean(label) && jobCertificateNames.has(normalizeValue(label))
    }

    const candidateSkillNameSet = useMemo(() => {
        const names = new Set<string>()
        const add = (name?: string | null) => {
            const n = normalizeValue(name)
            if (n) names.add(n)
        }
        if (candidateDetail?.areas_of_expertise?.length) {
            candidateDetail.areas_of_expertise.forEach((area) => {
                area.key_skills?.forEach((s) => add(s.name))
            })
        } else if (Array.isArray(candidate?.expertise)) {
            candidate.expertise.forEach((exp: any) => {
                exp.mustHave?.forEach((s: any) => add(s?.name))
                exp.niceToHave?.forEach((s: any) => add(s?.name))
            })
        }
        return names
    }, [candidateDetail?.areas_of_expertise, candidate?.expertise])

    const candidateSkillIdSet = useMemo(() => {
        const ids = new Set<number>()
        candidateDetail?.areas_of_expertise?.forEach((area) => {
            area.key_skills?.forEach((s) => {
                if (typeof s.id === "number") ids.add(s.id)
            })
        })
        return ids
    }, [candidateDetail?.areas_of_expertise])

    const missingJobSkillsByExpertise = useMemo(() => {
        const areas = jobDetails?.areas_of_expertise
        if (!jobId || !areas?.length) {
            return {
                extrasForExpertise: (_categoryName: string, _kind: "must" | "nice") => [] as { name: string }[],
                orphanMust: [] as { name: string }[],
                orphanNice: [] as { name: string }[],
            }
        }

        const skillMissingFromCandidate = (skill: { id: number; name: string }) => {
            const key = normalizeValue(skill.name)
            const hasById = typeof skill.id === "number" && candidateSkillIdSet.has(skill.id)
            const hasByName = Boolean(key) && candidateSkillNameSet.has(key)
            return Boolean(key) && !hasById && !hasByName
        }

        const mustByCat = new Map<string, { name: string }[]>()
        const niceByCat = new Map<string, { name: string }[]>()
        const mustSeenByCat = new Map<string, Set<string>>()
        const niceSeenByCat = new Map<string, Set<string>>()

        const append = (
            map: Map<string, { name: string }[]>,
            seenByCat: Map<string, Set<string>>,
            catKey: string,
            skill: { id: number; name: string }
        ) => {
            if (!skillMissingFromCandidate(skill)) return
            const key = normalizeValue(skill.name)
            let seen = seenByCat.get(catKey)
            if (!seen) {
                seen = new Set()
                seenByCat.set(catKey, seen)
            }
            if (seen.has(key)) return
            seen.add(key)
            const list = map.get(catKey) ?? []
            list.push({ name: skill.name })
            map.set(catKey, list)
        }

        for (const area of areas) {
            const catKey = normalizeValue(area.field_of_expertise?.name) || "_"
            for (const s of area.skills_must_have ?? []) append(mustByCat, mustSeenByCat, catKey, s)
            for (const s of area.skills_nice_to_have ?? []) append(niceByCat, niceSeenByCat, catKey, s)
        }

        const candidateNorms = new Set(
            (candidate?.expertise ?? [])
                .map((e: { category?: string }) => normalizeValue(e.category))
                .filter(Boolean)
        )

        const orphanMust: { name: string }[] = []
        const orphanNice: { name: string }[] = []
        const orphanMustSeen = new Set<string>()
        const orphanNiceSeen = new Set<string>()

        for (const area of areas) {
            const catKey = normalizeValue(area.field_of_expertise?.name)
            if (candidateNorms.has(catKey)) continue
            for (const s of area.skills_must_have ?? []) {
                if (!skillMissingFromCandidate(s)) continue
                const key = normalizeValue(s.name)
                if (!key || orphanMustSeen.has(key)) continue
                orphanMustSeen.add(key)
                orphanMust.push({ name: s.name })
            }
            for (const s of area.skills_nice_to_have ?? []) {
                if (!skillMissingFromCandidate(s)) continue
                const key = normalizeValue(s.name)
                if (!key || orphanNiceSeen.has(key)) continue
                orphanNiceSeen.add(key)
                orphanNice.push({ name: s.name })
            }
        }

        const extrasForExpertise = (categoryName: string, kind: "must" | "nice") => {
            const k = normalizeValue(categoryName)
            const map = kind === "must" ? mustByCat : niceByCat
            return map.get(k) ?? []
        }

        return { extrasForExpertise, orphanMust, orphanNice }
    }, [
        jobId,
        jobDetails?.areas_of_expertise,
        candidate?.expertise,
        candidateSkillNameSet,
        candidateSkillIdSet,
    ])

    if (!candidate) return <div className="p-8 text-center text-gray-500">Loading candidate profile...</div>

    const getExtraMustSkills = (categoryName: string, blockIndex: number) => {
        let list = missingJobSkillsByExpertise.extrasForExpertise(categoryName, "must")
        if (blockIndex === 0) {
            const seen = new Set(list.map((s) => normalizeValue(s.name)))
            for (const o of missingJobSkillsByExpertise.orphanMust) {
                const k = normalizeValue(o.name)
                if (k && !seen.has(k)) {
                    seen.add(k)
                    list = [...list, o]
                }
            }
        }
        return list
    }

    const getExtraNiceSkills = (categoryName: string, blockIndex: number) => {
        let list = missingJobSkillsByExpertise.extrasForExpertise(categoryName, "nice")
        if (blockIndex === 0) {
            const seen = new Set(list.map((s) => normalizeValue(s.name)))
            for (const o of missingJobSkillsByExpertise.orphanNice) {
                const k = normalizeValue(o.name)
                if (k && !seen.has(k)) {
                    seen.add(k)
                    list = [...list, o]
                }
            }
        }
        return list
    }

    const getAdditionalSkills = (exp: {
        mustHave?: Array<{ name: string; variant?: string }>
        niceToHave?: Array<{ name: string; variant?: string }>
    }) => {
        const seen = new Set<string>()
        const skills: Array<{ name: string; variant: string }> = []

        const add = (skill?: { name?: string; variant?: string }) => {
            if (!skill?.name || hasMatchingSkill(skill.name)) return
            const key = normalizeValue(skill.name)
            if (!key || seen.has(key)) return
            seen.add(key)
            skills.push({ name: skill.name, variant: skill.variant || "solid" })
        }

        exp.mustHave?.forEach(add)
        exp.niceToHave?.forEach(add)
        return skills
    }

    const sendAction = async (
        stage: string,
        status: string,
        successMessage: string,
        interview_date?: string
    ) => {
        if (!accessToken || !matchId) {
            toast.error("Missing required information to perform this action.")
            return
        }
        try {
            setIsSubmitting(true)
            const payload = { stage, status, ...(interview_date && { interview_date }) }
            const result = await updateRecruiterMatchAction(matchId, payload, accessToken)
            if (result.error) {
                toast.error(result.error)
            } else {
                toast.success(successMessage)
                onClose?.()
            }
        } catch (error) {
            const message = error instanceof Error ? error.message : "Failed to update."
            toast.error(message)
        } finally {
            setIsSubmitting(false)
        }
    }

    const openActionConfirm = (config: {
        stage: string
        status: string
        title: string
        message: string
        confirmLabel: string
        successMessage: string
        variant: "primary" | "danger"
    }) => {
        setPendingAction(config)
        setIsConfirmOpen(true)
    }

    const closeActionConfirm = () => {
        if (isSubmitting) return
        setIsConfirmOpen(false)
        setPendingAction(null)
    }

    const handleConfirmAction = async () => {
        if (!pendingAction) return
        const { stage, status, successMessage } = pendingAction
        setIsConfirmOpen(false)
        setPendingAction(null)
        await sendAction(stage, status, successMessage)
    }

    const handleRequestMatch = () =>
        openActionConfirm({
            stage: "Initial",
            status: "Requested",
            title: "Request match?",
            message: "This will send a match request to the candidate. This action cannot be undone.",
            confirmLabel: "Request match",
            successMessage: "Match requested successfully.",
            variant: "primary",
        })

    const handleAcceptMatch = () =>
        openActionConfirm({
            stage: "In review",
            status: "New",
            title: "Accept match?",
            message: "This will move the candidate into review. This action cannot be undone.",
            confirmLabel: "Accept match",
            successMessage: "Match accepted successfully.",
            variant: "primary",
        })

    const handleShortlisted = () =>
        openActionConfirm({
            stage: "Shortlisted",
            status: "Awaiting schedule",
            title: "Shortlist candidate?",
            message: "This will shortlist the candidate for this role. This action cannot be undone.",
            confirmLabel: "Shortlist",
            successMessage: "Candidate shortlisted successfully.",
            variant: "primary",
        })

    const handleNotSelected = () =>
        openActionConfirm({
            stage: "Verdict",
            status: "Rejected",
            title: "Not select candidate?",
            message: "This will mark the candidate as not selected. This action cannot be undone.",
            confirmLabel: "Not select",
            successMessage: "Candidate marked as not selected.",
            variant: "danger",
        })

    const handleDeclined = () =>
        openActionConfirm({
            stage: "Verdict",
            status: "Rejected",
            title: "Decline candidate?",
            message: "This will decline the candidate after the interview. This action cannot be undone.",
            confirmLabel: "Decline",
            successMessage: "Candidate declined successfully.",
            variant: "danger",
        })

    const handleHired = () =>
        openActionConfirm({
            stage: "Verdict",
            status: "Hired",
            title: "Hire candidate?",
            message: "This will mark the candidate as hired for this role. This action cannot be undone.",
            confirmLabel: "Hire",
            successMessage: "Candidate hired successfully.",
            variant: "primary",
        })

    const handleInterviewDateSelect = (date: Date) => {
        setIsDatePickerOpen(false)
        void executeInterviewDateUpdate(date)
    }

    const executeInterviewDateUpdate = async (date: Date) => {
        if (!accessToken || !matchId) return
        try {
            setIsSubmitting(true)
            const result = await updateRecruiterMatchAction(
                matchId,
                { stage: "Shortlisted", status: "Scheduled", interview_date: date.toISOString() },
                accessToken
            )
            if (result.error) {
                toast.error(result.error)
            } else {
                toast.success("Interview scheduled successfully.")
                onInterviewDateSaved?.(date.toISOString())
            }
        } catch (error) {
            const message = error instanceof Error ? error.message : "Failed to update interview date."
            toast.error(message)
        } finally {
            setIsSubmitting(false)
            setIsDatePickerOpen(false)
        }
    }

    const areInReviewActionsDisabled =
        isSubmitting || isFeedbackStatusLoading || !matchId || !hasFeedbackState

    const SkillTag = ({
        skill,
        isMatched,
        isMissingOnProfile = false,
    }: {
        skill: { name: string; variant: string }
        isMatched: boolean
        isMissingOnProfile?: boolean
    }) => {
        const baseClass =
            "inline-block max-w-full rounded-md px-3 py-1 text-sm font-medium leading-snug whitespace-normal break-words"

        if (isMissingOnProfile) {
            return (
                <span
                    className={`rounded-md ${baseClass} border border-gray-200 bg-white text-black`}
                    title="Required on the job — not listed on this candidate profile"
                >
                    {skill.name}
                </span>
            )
        }

        if (isMatched) {
            return (
                <span
                    className={`rounded-md ${baseClass} border-2 border-red-500 bg-white text-black`}
                    title="Candidate has this skill required by the job"
                >
                    {skill.name}
                </span>
            )
        }

        return (
            <span
                className={`rounded-md ${baseClass} border-2 border-[#ABD405] bg-white text-black`}
                title="Additional skill — not required by the job"
            >
                {skill.name}
            </span>
        )
    }

    return (
        <div className="rounded-md bg-white p-8 shadow-sm">
            {/* Areas of Expertise */}
            <section className="mb-6">
                <h2 className="text-subheading font-bold text-black mb-4">Areas of expertise</h2>
                <hr className="mb-6 border-gray-200" />

                {/* Mapping over candidate expertise categories */}
                {candidate.expertise?.map((exp: any, index: number) => {
                    const additionalSkills = getAdditionalSkills(exp)
                    const matchedMustSkills = (exp.mustHave ?? []).filter((skill: { name: string }) =>
                        hasMatchingSkill(skill.name)
                    )
                    const missingMustSkills = getExtraMustSkills(exp.category, index)
                    const matchedNiceSkills = (exp.niceToHave ?? []).filter((skill: { name: string }) =>
                        hasMatchingSkill(skill.name)
                    )
                    const missingNiceSkills = getExtraNiceSkills(exp.category, index)
                    const hasMustHaveSkills = matchedMustSkills.length > 0 || missingMustSkills.length > 0
                    const hasNiceToHaveSkills = matchedNiceSkills.length > 0 || missingNiceSkills.length > 0

                    return (
                    <div key={index} className="mb-10 last:mb-0">
                        <div className="mb-2 flex items-center justify-between">
                            <h3 className="text-paragraph font-bold text-black">{exp.category}</h3>
                            <span className="text-paragraph font-bold text-black">{exp.years} years of experience</span>
                        </div>
                        <div className="mb-4 text-sm">
                            <p className="text-small font-normal text-[#6B6B6B]">{exp.healthcare}</p>
                            <p className="text-small font-normal text-[#6B6B6B]">{exp.locations}</p>
                        </div>

                        {hasMustHaveSkills && (
                            <div className="mb-4">
                                <p className="mb-3 text-small font-semibold text-black">Must have skills</p>
                                <div className="w-[95%] flex flex-wrap gap-2">
                                    {matchedMustSkills.map((skill: any, idx: number) => (
                                        <SkillTag key={idx} skill={skill} isMatched={true} />
                                    ))}
                                    {missingMustSkills.map((skill, idx) => (
                                        <SkillTag
                                            key={`job-must-${index}-${idx}`}
                                            skill={{ name: skill.name, variant: "solid" }}
                                            isMatched={false}
                                            isMissingOnProfile
                                        />
                                    ))}
                                </div>
                            </div>
                        )}

                        {hasNiceToHaveSkills && (
                            <div className="mb-4">
                                <p className="mb-3 text-small font-semibold text-black">Nice to have skills</p>
                                <div className="w-[95%] flex flex-wrap gap-2">
                                    {matchedNiceSkills.map((skill: any, idx: number) => (
                                        <SkillTag key={idx} skill={skill} isMatched={true} />
                                    ))}
                                    {missingNiceSkills.map((skill, idx) => (
                                        <SkillTag
                                            key={`job-nice-${index}-${idx}`}
                                            skill={{ name: skill.name, variant: "solid" }}
                                            isMatched={false}
                                            isMissingOnProfile
                                        />
                                    ))}
                                </div>
                            </div>
                        )}

                        {additionalSkills.length > 0 && (
                            <div>
                                <p className="mb-3 text-small font-semibold text-black">Additional skills</p>
                                <div className="w-[95%] flex flex-wrap gap-2">
                                    {additionalSkills.map((skill, idx) => (
                                        <SkillTag key={`additional-${index}-${idx}`} skill={skill} isMatched={false} />
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                    )
                })}
            </section>

            {/* Team Management Experience */}
            <section className="mb-5">
                <p className="text-paragraph font-medium text-black mb-1">Team management experience</p>
                <p className="text-paragraph font-bold text-black">{candidate.teamExperience}</p>
            </section>

            {/* Personal, Educational and Language Information */}
            <div className="mb-12 grid grid-cols-1 gap-12 lg:grid-cols-2 lg:items-start">
                <div className="flex flex-col gap-12">
                    {/* Personal Information */}
                    <section>
                        <h2 className="mb-4 text-subheading font-bold text-black">Personal information</h2>
                        <hr className="mb-6 border-gray-200" />
                        <div className="grid grid-cols-2 gap-x-8 gap-y-6">
                            <div className="flex flex-col gap-1">
                                <p className="text-small text-gray-400">Gender</p>
                                {renderFieldValue(candidate.gender)}
                            </div>
                            <div className="flex flex-col gap-1">
                                <p className="text-small text-gray-400">Date of birth</p>
                                {renderFieldValue(candidate.dateOfBirth)}
                            </div>
                            {hasPersonalFieldValue(candidate.email) && (
                                <div className="flex flex-col gap-1">
                                    <p className="text-small text-gray-400">Email</p>
                                    {renderFieldValue(candidate.email)}
                                </div>
                            )}
                            {hasPersonalFieldValue(candidate.contactNumber) && (
                                <div className="flex flex-col gap-1">
                                    <p className="text-small text-gray-400">Contact number</p>
                                    {renderFieldValue(candidate.contactNumber)}
                                </div>
                            )}
                            <div className="flex flex-col gap-1">
                                <p className="text-small text-gray-400">Visa status</p>
                                {renderFieldValue(candidate.visaStatus, isVisaMatched, true)}
                            </div>
                            <div className="flex flex-col gap-1">
                                <p className="text-small text-gray-400">Driving license</p>
                                {renderFieldValue(candidate.drivingLicense, isDrivingLicenseMatched, true)}
                            </div>
                            <div className="flex flex-col gap-1">
                                <p className="text-small text-gray-400">Nationality</p>
                                {renderFieldValue(candidate.nationality, isNationalityMatched, true)}
                            </div>
                        </div>
                    </section>

                    {/* Educational Information */}
                    <section>
                        <h2 className="mb-4 text-subheading font-bold text-black">Educational information</h2>
                        <hr className="mb-6 border-gray-200" />
                        <div className="space-y-6">
                            {candidate.education?.map((edu: any, index: number) => (
                                <div key={index} className="flex items-start justify-between gap-6">
                                    <div>
                                        <span className={getBadgeClass(isEducationMatched(index))}>
                                            {edu.degree}
                                        </span>
                                        <p className="mt-2 text-small text-gray-600">{edu.university}</p>
                                    </div>
                                    <span className="shrink-0 text-paragraph font-bold text-black">{edu.years}</span>
                                </div>
                            ))}
                        </div>
                    </section>
                </div>

                {/* Language Information */}
                <section>
                    <h2 className="mb-4 text-subheading font-bold text-black">Language information</h2>
                    <hr className="mb-6 border-gray-200" />
                    <div className="grid grid-cols-3 gap-x-6">
                        <p className="text-small text-gray-400">Language</p>
                        <p className="text-small text-gray-400">Proficiency level</p>
                        <p className="text-small text-gray-400">Skills</p>
                    </div>
                    <div className="mt-4 space-y-6">
                        {candidate.languages?.map((item: any, i: number) => {
                            const matchingLanguage = getMatchingLanguageRequirement(item.lang)
                            const isLanguageMatched = Boolean(matchingLanguage)
                            const isSpeakingMatched =
                                Boolean(matchingLanguage?.speaking_level) &&
                                isSameValue(item.level, matchingLanguage?.speaking_level)
                            const isReadingWritingMatched =
                                Boolean(matchingLanguage?.reading_writing_level) &&
                                isSameValue(item.skills, matchingLanguage?.reading_writing_level)

                            return (
                                <div key={i} className="grid grid-cols-3 gap-x-6 items-start">
                                    <div className="min-w-0">
                                        {renderFieldValue(item.lang, isLanguageMatched)}
                                    </div>
                                    <div className="min-w-0">
                                        {renderFieldValue(item.level, isSpeakingMatched)}
                                    </div>
                                    <div className="min-w-0">
                                        {renderFieldValue(item.skills, isReadingWritingMatched)}
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                </section>
            </div>

            {/* Certification Information */}
            <section className="mb-12">
                <h2 className="mb-4 text-subheading font-bold text-black">Certification information</h2>
                <hr className="mb-6 border-gray-200" />
                <div className="space-y-8">
                    {candidateCertificates?.map((cert: any, index: number) => (
                        <div key={index} className="flex justify-between">
                            <div>
                                <span className={getBadgeClass(isCertificationMatched(cert.name))}>{cert.name}</span>
                                <p className="mt-2 text-small text-gray-600">{cert.issuer}</p>
                            </div>
                            <span className="text-paragraph font-bold text-black">{cert.years}</span>
                        </div>
                    ))}
                </div>
            </section>

            {/* Action Buttons — by stage */}
            {showActions && <div className="sticky bottom-0 z-10 mt-12 flex items-center justify-between border-t border-gray-200 bg-white pt-8">
                <button
                    onClick={onClose}
                    className="rounded-md border border-gray-300 px-12 py-3 text-sm font-bold text-gray-600 hover:bg-gray-50"
                >
                    Close
                </button>
                {!isCompanyHiringManager && <div className="flex gap-4">
                    {/* Initial: Request match */}
                    {(normalizedStage === null || normalizedStage === "In matches" || normalizedStage === "Initial") && (
                        <button
                            onClick={handleRequestMatch}
                            disabled={isSubmitting || !matchId}
                            className="btn-cta rounded-md px-12 py-3 text-sm font-bold disabled:opacity-50"
                        >
                            {isSubmitting ? "Processing..." : "Request match"}
                        </button>
                    )}

                    {/* Incoming request: Accept match */}
                    {normalizedStage === "Incoming request" && (
                        <>
                            <button
                                onClick={handleNotSelected}
                                disabled={isSubmitting || !matchId}
                                className="btn-cta rounded-md px-12 py-3 text-sm font-bold disabled:opacity-50"
                            >
                                {isSubmitting ? "Processing..." : "Not select"}
                            </button>
                            <button
                                onClick={handleAcceptMatch}
                                disabled={isSubmitting || !matchId}
                                className="btn-cta rounded-md px-12 py-3 text-sm font-bold disabled:opacity-50"
                            >
                                {isSubmitting ? "Processing..." : "Accept match"}
                            </button>
                        </>
                    )}

                    {/* In review: Shortlisted (disabled until feedback) */}
                    {normalizedStage === "In review" && (
                        <>
                            <button
                                onClick={handleNotSelected}
                                disabled={areInReviewActionsDisabled}
                                className="btn-cta rounded-md px-12 py-3 text-sm font-bold disabled:opacity-50"
                            >
                                {isSubmitting ? "Processing..." : "Not select"}
                            </button>
                            <button
                                onClick={handleShortlisted}
                                disabled={areInReviewActionsDisabled}
                                className="btn-cta rounded-md px-12 py-3 text-sm font-bold disabled:opacity-50"
                            >
                                {isSubmitting ? "Processing..." : "Shortlist"}
                            </button>
                        </>
                    )}

                    {/* Shortlisted stage */}
                    {normalizedStage === "Shortlisted" && (
                        <>
                            {hasInterviewDate ? (
                                <>
                                    <button
                                        onClick={handleDeclined}
                                        disabled={isSubmitting || !matchId || !isInterviewInPast}
                                        className="btn-cta rounded-md px-12 py-3 text-sm font-bold disabled:opacity-50"
                                    >
                                        {isSubmitting ? "Processing..." : "Decline"}
                                    </button>
                                    <button
                                        onClick={handleHired}
                                        disabled={isSubmitting || !matchId || !isInterviewInPast}
                                        className="btn-cta rounded-md px-12 py-3 text-sm font-bold disabled:opacity-50"
                                    >
                                        {isSubmitting ? "Processing..." : "Hire"}
                                    </button>
                                </>
                            ) : (
                                <>
                                    <button
                                        onClick={handleNotSelected}
                                        disabled={isSubmitting || !matchId}
                                        className="btn-cta rounded-md px-12 py-3 text-sm font-bold disabled:opacity-50"
                                    >
                                        {isSubmitting ? "Processing..." : "Not select"}
                                    </button>
                                    <button
                                        onClick={() => setIsDatePickerOpen(true)}
                                        disabled={isSubmitting || !matchId}
                                        className="btn-cta rounded-md px-12 py-3 text-sm font-bold disabled:opacity-50"
                                    >
                                        Interview date
                                    </button>
                                </>
                            )}
                        </>
                    )}
                </div>}
            </div>}

            {isDatePickerOpen && (
                <DatePicker
                    isOpen={true}
                    onClose={() => setIsDatePickerOpen(false)}
                    onUpdate={handleInterviewDateSelect}
                    initialDate={interviewDate ? new Date(interviewDate) : undefined}
                    label="Select interview date"
                />
            )}

            <ConfirmActionModal
                isOpen={isConfirmOpen && !!pendingAction}
                title={pendingAction?.title ?? ""}
                message={pendingAction?.message ?? ""}
                confirmLabel={pendingAction?.confirmLabel}
                variant={pendingAction?.variant}
                isLoading={isSubmitting}
                onConfirm={handleConfirmAction}
                onCancel={closeActionConfirm}
            />
        </div>
    )
}
